import { Box, Button, TextField, Typography } from '@mui/material';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { formatTime, longDate } from '../../shared/taxonomy';
import { DateField, TimeField } from './DateTimeFields';
import { FIRST_DAY, LAST_DAY } from './dateTime';
import { fieldSx } from './formStyles';
import PlaceField from './PlaceField';
import { isComplete, SLOT_MINUTES, type PersonalDraft } from './personalEvents';

/**
 * The form for an event the user is adding themselves.
 *
 * One component, three placements: a dialog from the header button, a panel
 * beside the day when it was opened by clicking an empty slot, and on a phone a
 * sheet half up under the day, whose own bar holds Save and Cancel. The second is
 * the reason it is a panel at all — a dialog would cover the schedule, and the
 * whole point of creating an event by clicking a gap is to see the gap you are
 * filling.
 *
 * Title, date and time are required; everything else is optional, because the
 * common case is someone blocking out two hours for dinner and moving on.
 */

interface Props {
  draft: PersonalDraft;
  onChange: (draft: PersonalDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Present when editing rather than creating. */
  onDelete?: () => void;
  /** The panel form gets a heading; the dialog supplies its own. */
  heading?: string;
  /**
   * In the phone's sheet, Save and Cancel live in the bar pinned above the
   * form, and the title waits to be tapped: focusing it would raise the
   * keyboard over the day the reader is looking at.
   */
  inSheet?: boolean;
}

export default function PersonalEventForm({
  draft,
  onChange,
  onSave,
  onCancel,
  onDelete,
  heading,
  inSheet = false,
}: Props) {
  const setStart = (minutes: number) => {
    // Moving the start past the end would invert the block; carry the end.
    const length = Math.max(draft.endMinutes - draft.startMinutes, SLOT_MINUTES);
    onChange({ ...draft, startMinutes: minutes, endMinutes: minutes + length });
  };

  const setEnd = (minutes: number) =>
    onChange({ ...draft, endMinutes: Math.max(minutes, draft.startMinutes + 5) });

  const ready = isComplete(draft);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) onSave();
      }}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.6 }}
    >
      {heading && (
        <Typography sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 18 }}>
          {heading}
        </Typography>
      )}

      <TextField
        label="Title"
        required
        autoFocus={!inSheet}
        value={draft.title}
        onChange={(e) => onChange({ ...draft, title: e.target.value })}
        slotProps={{ htmlInput: { maxLength: 120 } }}
        sx={fieldSx}
      />

      {/* Typed, with a list underneath. The dates on offer are the three weeks
          this app plans; the times are quarter-hours. */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <DateField value={draft.dayKey} onChange={(dayKey) => onChange({ ...draft, dayKey })} />
        {/**
         * From and To are one control in two boxes, and they wrap as one.
         *
         * Left to itself the row breaks wherever it runs out of width, which
         * put the start time up beside the date and stranded the end time on a
         * line of its own — a range you have to read around a corner. Holding
         * them in their own box means the date gives way first, and the two
         * times stay side by side at every width.
         */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flex: '1 1 200px',
            minWidth: 200,
            alignItems: 'flex-start',
          }}
        >
          <TimeField label="From" value={draft.startMinutes} onChange={setStart} />
          <TimeField
            label="To"
            value={draft.endMinutes}
            onChange={setEnd}
            from={draft.startMinutes}
          />
        </Box>
      </Box>

      <PlaceField
        value={draft.venue}
        coords={draft.coords}
        onChange={(venue, coords) => onChange({ ...draft, venue, coords })}
        // Says what picking a suggestion buys, since typing freely also works.
        hint={(located) =>
          located
            ? 'Located — travel time to and from this will be estimated.'
            : 'Optional. Pick a suggestion to get travel times to and from it.'
        }
      />

      <TextField
        label="Description"
        multiline
        minRows={2}
        value={draft.description}
        onChange={(e) => onChange({ ...draft, description: e.target.value })}
        slotProps={{ htmlInput: { maxLength: 2000 } }}
        sx={fieldSx}
      />

      <Typography sx={{ fontSize: 11.5, opacity: 0.55 }}>
        {longDate(draft.dayKey).weekday}, {longDate(draft.dayKey).full} ·{' '}
        {formatTime(draft.startMinutes)} – {formatTime(draft.endMinutes)}
        {(draft.dayKey < FIRST_DAY || draft.dayKey > LAST_DAY) && (
          <Box component="span" sx={{ display: 'block', color: '#ef3d23', fontWeight: 700 }}>
            Outside Climate Week. Pick a date from September 13 to October 3.
          </Box>
        )}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
        {onDelete && (
          <Button
            onClick={onDelete}
            sx={{ mr: 'auto', color: '#ef3d23', fontWeight: 700, fontSize: 13 }}
          >
            Delete
          </Button>
        )}
        {!inSheet && (
          <Button onClick={onCancel} sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600 }}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!ready}
          sx={{
            // The sheet's bar has the Save; this one stays in the form so Enter still submits it.
            display: inSheet ? 'none' : undefined,
            background: BRAND.parchment,
            color: BRAND.mocha,
            fontWeight: 700,
            borderRadius: 999,
            px: 2.6,
            '&:hover': { background: '#fff' },
            '&.Mui-disabled': {
              background: 'rgba(243,238,231,0.25)',
              color: 'rgba(54,47,42,0.5)',
            },
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}
